import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useAxios } from 'configs/requests';
import { DeviceEventType, FulfillmentStatus, PatientTabs, ShippingSpeed } from 'constants/enums';
import { Error, Loading } from 'components/Fetch';
import { getPath } from 'routes';
import { useAppSelector } from 'hooks';
import { formatDeliveryWindow } from 'utils/Devices';
import { formatEstimatedShipDay, getFulfillmentStepDetailLabel } from 'utils/fulfillment';
import { getAmazonTrackingUrl } from 'utils/tracking-url';
import { ExternalLink } from 'components/UI/ExternalLink';

import { FulfillmentStepper } from './FulfillmentStepper';

interface IDeviceHistoryProps {
  device: IDeviceListItem;
}

/** The JSON an Ordered / Shipped event stores; address fields are strings, the rest optional. */
interface IShippingEventContent extends Record<string, unknown> {
  purchaseOrderId?: string;
  orderId?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  stateProvince?: string;
  countryCode?: string;
  postalCode?: string;
  trackingNumber?: string;
  amazonTrackingNumber?: string;
  shippingSpeed?: ShippingSpeedValue;
  promisedDelivery?: IDeliveryWindow | null;
}

interface IFulfillmentEventContent {
  provider?: string;
  status?: string;
  providerStatus?: string;
  occurredAt?: string;
  estimatedShipDate?: string;
}

const DeviceHistory = ({ device }: IDeviceHistoryProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const locale = useAppSelector((state) => state.intl.locale);
  const [{ data: events, loading, error }, getHistory] = useAxios(
    {
      url: `/clinician-gateway/device/${device.deviceId}/history`
    },
    { useCache: false }
  );

  /** Ordered events carry the speed and the preview window the order was placed with. */
  const renderShippingSpeed = (parsedContent: IShippingEventContent) => {
    if (!parsedContent.shippingSpeed) return null;
    const window = parsedContent.promisedDelivery
      ? formatDeliveryWindow(
          parsedContent.promisedDelivery.start,
          parsedContent.promisedDelivery.end,
          locale
        )
      : null;
    return (
      <>
        <div
          data-test="device-history-shipping-speed"
          data-test-value={parsedContent.shippingSpeed}
        >
          {formatMessage({ id: 'devices.shippingSpeed.title' })}:{' '}
          {formatMessage({
            id:
              parsedContent.shippingSpeed === ShippingSpeed.EXPEDITED
                ? 'devices.shippingSpeed.expedited'
                : 'devices.shippingSpeed.standard'
          })}
        </div>
        {window && (
          <div data-test="device-history-est-delivery">
            {formatMessage({ id: 'devices.estDelivery' })}: {window}
          </div>
        )}
      </>
    );
  };

  /**
   * Amazon's own number (TBA…) links to Amazon tracking: on the carrier number when the two are
   * equal, otherwise on its own line.
   */
  const renderTracking = (parsedContent: IShippingEventContent) => {
    const { trackingNumber, amazonTrackingNumber } = parsedContent;
    const sameNumber = !!amazonTrackingNumber && amazonTrackingNumber === trackingNumber;
    const amazonLink = (number: string) => (
      <ExternalLink
        href={getAmazonTrackingUrl(number)}
        title={number}
        data-test="device-history-amazon-tracking"
      >
        {number}
      </ExternalLink>
    );
    return (
      <>
        {trackingNumber && (
          <div>
            {formatMessage({ id: 'trackingNumberKey' })}:{' '}
            {sameNumber ? amazonLink(trackingNumber) : trackingNumber}
          </div>
        )}
        {amazonTrackingNumber && !sameNumber && (
          <div className="event-content-plain">
            {formatMessage({ id: 'devices.amazonTracking' })}: {amazonLink(amazonTrackingNumber)}
          </div>
        )}
      </>
    );
  };

  const renderFulfillmentContent = (parsedContent: IFulfillmentEventContent) => {
    if (!parsedContent.status) return null;
    const step = getFulfillmentStepDetailLabel(
      formatMessage,
      parsedContent.status,
      parsedContent.providerStatus
    );
    const estimatedShipDay =
      parsedContent.status === FulfillmentStatus.PROCESSING
        ? formatEstimatedShipDay(parsedContent.estimatedShipDate, locale)
        : null;
    const parts = [
      parsedContent.provider ? `${parsedContent.provider}: ${step}` : step,
      estimatedShipDay &&
        formatMessage({ id: 'devices.fulfillment.estShip' }, { date: estimatedShipDay })
    ].filter(Boolean);
    return (
      <div
        className={`event-content event-content-plain${
          parsedContent.status === FulfillmentStatus.EXCEPTION ? ' text-danger' : ''
        }`}
        data-test="device-history-fulfillment"
        data-test-value={parsedContent.status}
      >
        <div>{parts.join(' · ')}</div>
      </div>
    );
  };

  const renderShippingContent = (
    parsedContent: IShippingEventContent,
    orderItems?: Array<{ sku: string; name: string; qty: number }>
  ) => (
    <div className="event-content">
      {parsedContent.purchaseOrderId && (
        <div>
          {formatMessage({ id: 'merchantOrderId' })}: {parsedContent.purchaseOrderId}
        </div>
      )}
      {parsedContent.orderId && (
        <div>
          {formatMessage({ id: 'purchaseOrderKey' })}: {parsedContent.orderId}
        </div>
      )}
      {orderItems?.map((item) => (
        <div key={item.sku}>
          {item.sku} {item.name} x {item.qty}
        </div>
      ))}
      <div>{parsedContent.streetAddress1}</div>
      {parsedContent.streetAddress2 && <div>{parsedContent.streetAddress2}</div>}
      <div>
        {parsedContent.city}, {parsedContent.stateProvince}, {parsedContent.countryCode}
      </div>
      <div>
        {formatMessage({ id: 'zipCodeKey' })}: {parsedContent.postalCode}
      </div>
      {renderTracking(parsedContent)}
      {renderShippingSpeed(parsedContent)}
    </div>
  );

  const renderEvent = (event: IDeviceEvent) => {
    let content: JSX.Element | null = null;
    let label: string = '';
    const parsedEventContent = event.eventContent ? JSON.parse(event.eventContent) : {};
    switch (event.eventType) {
      case DeviceEventType.ORDERED: {
        label = formatMessage({ id: 'orderedKey' });
        content = renderShippingContent(parsedEventContent);
        break;
      }
      case DeviceEventType.SHIPPED: {
        label = formatMessage({ id: 'shippedKey' });
        content = renderShippingContent(parsedEventContent);
        break;
      }
      case DeviceEventType.ASSIGNED: {
        label = formatMessage({ id: 'assignedKey' });
        const name = parsedEventContent.name;
        const mrn = parsedEventContent.alternateIdKey;
        const dob = parsedEventContent.dobKey;
        const redirectToPatient = (uid: string) => {
          navigate(getPath(true).dashboard.patient(uid, PatientTabs.DEVICES));
        };
        content = (
          <div
            className="event-content assign"
            onClick={() => redirectToPatient(parsedEventContent.uid)}
          >
            <div>{name}</div>
            {mrn && (
              <div>
                {formatMessage({ id: 'mrnKey' })}: {mrn}
              </div>
            )}
            <div>
              {formatMessage({ id: 'dobKey' })}: {dob}
            </div>
          </div>
        );
        break;
      }
      case DeviceEventType.UNASSIGNED: {
        label = formatMessage({ id: 'unassignedKey' });
        break;
      }
      case DeviceEventType.FIRST_TRANSMISSION: {
        label = formatMessage({ id: 'firstTransmissionKey' });
        break;
      }
      case DeviceEventType.REPLACED_BY: {
        label = formatMessage({ id: 'replacedKey' });
        const imei = parsedEventContent.imei;
        content = (
          <div className="event-content">
            <div>
              {formatMessage({ id: 'imeiKey' })}: {imei}
            </div>
          </div>
        );
        break;
      }
      case DeviceEventType.REPLACES: {
        label = formatMessage({ id: 'replacesKey' });
        const imei = parsedEventContent.imei;
        content = (
          <div className="event-content">
            <div>
              {formatMessage({ id: 'imeiKey' })}: {imei}
            </div>
            {renderShippingSpeed(parsedEventContent)}
          </div>
        );
        break;
      }
      case DeviceEventType.REPLENISHMENT_SHIPPED: {
        label = formatMessage({ id: 'replenishmentShippedKey' });
        content = renderShippingContent(parsedEventContent, parsedEventContent.orderItems ?? []);
        break;
      }
      case DeviceEventType.CANCELLED: {
        label = formatMessage({ id: 'cancelledKey' });
        break;
      }
      case DeviceEventType.DELIVERED: {
        label = formatMessage({ id: 'deliveredKey' });
        break;
      }
      case DeviceEventType.FULFILLMENT_STATUS: {
        label = formatMessage({ id: 'devices.fulfillment.historyLabel' });
        content = renderFulfillmentContent(parsedEventContent);
        break;
      }
      case DeviceEventType.SUSPENDED: {
        // One event type covers both, so eventContent carries the distinction. Absent content
        // means a plain suspend: every caller but the archive path omits it.
        label = formatMessage({
          id:
            parsedEventContent.simReversible === false
              ? 'deviceDeactivatedKey'
              : 'deviceSuspendedKey'
        });
        break;
      }
      case DeviceEventType.REACTIVATED: {
        label = formatMessage({ id: 'deviceReactivatedKey' });
        break;
      }
      case DeviceEventType.AUTO_REACTIVATED: {
        label = formatMessage({ id: 'deviceAutoReactivatedKey' });
        break;
      }
      default: {
        label = event.eventType;
        break;
      }
    }

    return (
      <div className="event" key={`event-${event.id}`}>
        <div className="event-date">
          {DateTime.fromMillis(event.eventDate)
            .setLocale(locale)
            .toLocaleString(DateTime.DATE_SHORT)}
        </div>
        <div className="event-label">{label}</div>
        {content}
      </div>
    );
  };

  return (
    <div className="device-history" key={device.deviceId}>
      {loading && <Loading />}
      {error && <Error retry={getHistory} />}
      <FulfillmentStepper device={device} />
      <div className="outer-timeline">
        {events?.length && events.map((event: IDeviceEvent) => renderEvent(event))}
      </div>
    </div>
  );
};

export default DeviceHistory;
